import {useState, useMemo} from "react";
import {useSelector} from "react-redux";

import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import MainActions from "@/components/MainActions";

import {phasesOptions} from "@/pages/Manager/Match/handleExercise";
import {COLORS, SERIES} from "@/style/utils";

import {groupTrainingExercises} from "@/utils/trainingExercisePhases";
import "./ExercisesTab.scss";

// Colore stabile per tipo di esercizio, dalla serie dei grafici
const _typeCache = {};
let _typeIdx = 0;

const typeColor = (type) => {
	if (!type) return COLORS.textMuted;
	if (!_typeCache[type]) {
		_typeCache[type] = SERIES[_typeIdx % SERIES.length];
		_typeIdx++;
	}
	return _typeCache[type];
};

// ── Summary strip ─────────────────────────────────────────────────────────────

function SummaryStrip({totalMin, count, activePhases, prevalentType}) {
	const kpis = [
		{v: totalMin ? `${totalMin}'` : "—", l: "Durata totale"},
		{v: count || "—", l: "Esercizi"},
		{v: `${activePhases} / 4`, l: "Fasi attive"},
		{v: prevalentType || "—", l: "Tipo prevalente"},
	];
	return (
		<div className="ext-summary">
			{kpis.map((k, i) => (
				<div key={i} className="ext-summary__item">
					<span className="ext-summary__value">{k.v}</span>
					<span className="ext-summary__label">{k.l}</span>
				</div>
			))}
		</div>
	);
}

// ── Phase timeline ─────────────────────────────────────────────────────────────

function PhaseTimeline({phases}) {
	const total = phases.reduce((s, p) => s + p.duration, 0);
	if (!total) return null;
	return (
		<div className="ext-timeline">
			{phases.map((p, i) => {
				if (!p.duration) return null;
				const pct = Math.round((p.duration / total) * 100);
				return (
					<div
						key={p.id}
						className="ext-timeline__block"
						style={{
							flex: p.duration,
							"--series": p.unassigned ? "var(--color-text-muted)" : SERIES[i],
						}}
						title={`${p.label}: ${p.duration}'`}
					>
						<span className="ext-timeline__name">
							{p.unassigned ? "Da assegnare" : `M${p.id}`}
						</span>
						<span className="ext-timeline__dur">{p.duration}&#8242;</span>
						<span className="ext-timeline__pct">{pct}%</span>
					</div>
				);
			})}
		</div>
	);
}

// ── Exercise card ─────────────────────────────────────────────────────────────

function ExerciseCard({te, isFirst, isLast, onEdit, onDelete, onReorder}) {
	const userRole = useSelector((s) => s?.user?.roleName);
	const [open, setOpen] = useState(false);
	const hasBody =
		te.description || te.exercise?.description || te.exercise?.imageUrl;

	return (
		<div className="ext-card">
			<div className="ext-card__row">
				<span className="ext-card__num">{te.sorting}</span>

				<span
					className="ext-card__type"
					style={{"--series": typeColor(te.exercise?.exerciseType)}}
				>
					{te.exercise?.exerciseType || "—"}
				</span>

				<div className="ext-card__info">
					<span className="ext-card__name">
						{te.exercise?.name || te.description || "Esercizio senza nome"}
					</span>
					{te.exercise?.objectives && (
						<span className="ext-card__obj">{te.exercise.objectives}</span>
					)}
				</div>

				<span className="ext-card__dur">
					{te.duration ? `${te.duration}'` : "—"}
				</span>

				{userRole !== "Analyst" && (
					<div className="ext-card__actions">
						<button
							className="ext-card__btn"
							onClick={() => onReorder(te, "up")}
							disabled={isFirst}
							title="Sposta su"
						>
							<ArrowUpwardIcon sx={{fontSize: 12}} />
						</button>
						<button
							className="ext-card__btn"
							onClick={() => onReorder(te, "down")}
							disabled={isLast}
							title="Sposta giù"
						>
							<ArrowDownwardIcon sx={{fontSize: 12}} />
						</button>
						<button
							className="ext-card__btn"
							onClick={() => onEdit(te)}
							title="Modifica"
						>
							<EditIcon sx={{fontSize: 12}} />
						</button>
						<button
							className="ext-card__btn ext-card__btn--del"
							onClick={() => onDelete(te)}
							title="Elimina"
						>
							<CloseIcon sx={{fontSize: 12}} />
						</button>
					</div>
				)}

				{hasBody && (
					<button
						className="ext-card__toggle"
						onClick={() => setOpen((v) => !v)}
					>
						{open ? (
							<ExpandLessIcon sx={{fontSize: 16}} />
						) : (
							<ExpandMoreIcon sx={{fontSize: 16}} />
						)}
					</button>
				)}
			</div>

			{open && (
				<div className="ext-card__body">
					{te.exercise?.imageUrl && (
						<img className="ext-card__img" src={te.exercise.imageUrl} alt="" />
					)}
					{(te.description || te.exercise?.description) && (
						<p className="ext-card__desc">
							{te.description || te.exercise.description}
						</p>
					)}
				</div>
			)}
		</div>
	);
}

// ── Phase section ─────────────────────────────────────────────────────────────

function PhaseSection({phase, idx, onAdd, onEdit, onDelete, onReorder}) {
	const userRole = useSelector((s) => s?.user?.roleName);

	const typeSummary = useMemo(() => {
		const map = {};
		phase.exercises.forEach((te) => {
			const t = te.exercise?.exerciseType || "—";
			map[t] = (map[t] || 0) + (te.duration || 0);
		});
		return Object.entries(map).sort((a, b) => b[1] - a[1]);
	}, [phase.exercises]);

	return (
		<div className="ext-phase">
			<div
				className="ext-phase__header"
				style={{
					"--series": phase.unassigned
						? "var(--color-text-muted)"
						: SERIES[idx],
				}}
			>
				<div className="ext-phase__hinfo">
					<span className="ext-phase__title">{phase.label}</span>
					{phase.exercises.length > 0 && (
						<span className="ext-phase__meta">
							{phase.duration}&#8242; &middot; {phase.exercises.length}{" "}
							{phase.exercises.length === 1 ? "esercizio" : "esercizi"}
						</span>
					)}
					<div className="ext-phase__chips">
						{typeSummary.map(([type, dur]) => (
							<span
								key={type}
								className="ext-phase__chip"
								style={{"--series": typeColor(type)}}
							>
								{type} · {dur}&#8242;
							</span>
						))}
					</div>
				</div>

				{userRole !== "Analyst" && !phase.unassigned && (
					<MainActions
						actions={[
							{
								icon: <AddIcon />,
								label: "Aggiungi",
								variant: "outlined",
								onClick: () => onAdd(phase.id),
							},
						]}
					/>
				)}
			</div>

			<div className="ext-phase__body">
				{phase.exercises.length === 0 ? (
					<div className="ext-phase__empty">Nessun esercizio</div>
				) : (
					phase.exercises.map((te, i) => (
						<ExerciseCard
							key={te.id}
							te={te}
							isFirst={i === 0}
							isLast={i === phase.exercises.length - 1}
							onEdit={onEdit}
							onDelete={onDelete}
							onReorder={onReorder}
						/>
					))
				)}
			</div>
		</div>
	);
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function ExercisesTab({
	exercises,
	onAdd,
	onEdit,
	onDelete,
	onReorder,
}) {
	const phases = useMemo(
		() => groupTrainingExercises(exercises, phasesOptions),
		[exercises],
	);

	const totalMin = useMemo(
		() => phases.reduce((s, p) => s + p.duration, 0),
		[phases],
	);

	const activePhases = useMemo(
		() => phases.filter((p) => !p.unassigned && p.exercises.length > 0).length,
		[phases],
	);

	const prevalentType = useMemo(() => {
		const map = {};
		exercises.forEach((te) => {
			const t = te.exercise?.exerciseType;
			if (t) map[t] = (map[t] || 0) + (te.duration || 0);
		});
		return Object.entries(map).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
	}, [exercises]);

	return (
		<div className="ext-wrap">
			<SummaryStrip
				totalMin={totalMin}
				count={exercises.length}
				activePhases={activePhases}
				prevalentType={prevalentType}
			/>

			<PhaseTimeline phases={phases} />

			{[...phases]
				.sort((a, b) => Number(b.unassigned) - Number(a.unassigned))
				.map((phase) => (
					<PhaseSection
						key={phase.id}
						phase={phase}
						idx={phases.findIndex((p) => p.id === phase.id)}
						onAdd={onAdd}
						onEdit={onEdit}
						onDelete={onDelete}
						onReorder={onReorder}
					/>
				))}
		</div>
	);
}
